import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect, useSelector } from "react-redux";
import { isArray } from "lodash";

import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "@mui/material/Avatar";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import ShareIcon from "@mui/icons-material/Share";
import LanguageIcon from "@mui/icons-material/Language";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import TextFieldGroup from "../common/TextFieldGroup";
import Spinner from "../ui/spinner/Spinner";
import Modal from "../common/Modal";
import ApiRequest from "../../actions/ApiRequest";
import ShareModal from "./ShareModal";
import UsersCompanies from "./UsersCompanies";

import translate from "../../i18n/translate";
import { intl } from "./../../i18n/provider";

import { getDiscounts } from "./../../actions/discountsAction";
import {
  getSellerData,
  updateProfile,
  getAnotherSellerData,
} from "../../actions/userAction";
import { getSellers } from "../../actions/sellerCreditsAction";

import "./styles.css";
import Settings from "../ui/Settings/Settings";
import { useIsAdmin } from "../../shared/hooks/ppHooks.js";
import ChangeProfile from "./ChangeProfile";
import Toast from "components/common/Toast";
import ContactOptions from "./ContactOptions";
import { profileData } from "./profileData";
import { useHistory } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const Profile = ({
  getSellerData,
  userDetails,
  getAnotherSellerData,
  userDetailsLoading,
  sellers,
  getSellers,
  updateProfile,
  user,
  getDiscounts,
  discounts,
  isDarktheme,
}) => {
  const hide = localStorage.getItem("hide companies");
  const history = useHistory();

  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [entryChoiceOpen, setEntryChoiceOpen] = useState(false);
  const [hasChosenUpdate, setHasChosenUpdate] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [seller, setSeller] = useState(userDetails);
  const [filteredData, setFilteredData] = useState(sellers);
  const [discountData, setDiscountData] = useState([]);

  const [shareModal, setShareModal] = useState(false);
  const [updateData, setUpdateData] = useState({});
  const [selectedSellerOption, setSelectedSellerOption] = useState(null);
  const { isAdmin, isReseller } = useIsAdmin();
  const sellerId = useSelector((state) => state.auth.user?.id);

  useEffect(() => {
    try {
      // Load immediately for regular users; for admin/reseller only after choosing UPDATE USER
      const shouldLoad = !(isAdmin || isReseller) || hasChosenUpdate;
      if (shouldLoad) {
        getSellerData(user.id);
        getSellers(isReseller && sellerId);
        getDiscounts();
      }
    } catch (e) {
      // Error loading profile data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChosenUpdate, isAdmin, isReseller, sellerId]);

  // Open entry choice only once on mount for admin/reseller
  useEffect(() => {
    if (isAdmin || isReseller) {
      setEntryChoiceOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select seller from navigation state
  useEffect(() => {
    const autoSelectSeller = async () => {
      try {
        const locationState = history?.location?.state;
        if (!locationState?.seller_id) {
          return;
        }

        // Wait for sellers data to be available
        if (!filteredData || filteredData.length === 0) {
          return;
        }

        const selectedSeller = filteredData.find(s => String(s.seller_id) === String(locationState.seller_id));
        
        if (selectedSeller) {
          const selectedOption = {
            value: selectedSeller.seller_id,
            label: `${selectedSeller.name} ${selectedSeller.seller_id}`,
            ...selectedSeller
          };
          
          setSelectedSellerOption(selectedOption);
          await getAnotherSellerData(selectedSeller.seller_id, setSeller);
          await getDiscounts(
            user?.id !== selectedSeller.seller_id,
            selectedSeller.seller_id,
            setDiscountData
          );
          
          // Clear the state after successful processing
          if (history?.location?.state) {
            history.replace({ pathname: '/Profile', state: undefined });
          }
        }
      } catch (error) {
        // Auto-selection failed, show default profile
      }
    };

    autoSelectSeller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  useEffect(() => {
    // Update seller state when userDetails changes
    if (userDetails) {
      console.log('👤 Setting seller from userDetails:', userDetails);
      setSeller(userDetails);
    }
  }, [userDetails]);

  useEffect(() => {
    if (isArray(discounts) && discounts) {
      let discountsArr = [];
      if (hide === "true") {
        discountsArr = discounts.filter((item) => item.location !== "ISR");
        setDiscountData(discountsArr);
        return;
      }
      setDiscountData(discounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discounts]);

  useEffect(() => {
    if (sellers.length) {
      setFilteredData(sellers);
    }
  }, [sellers]);

  const mockChangeProfileData = useMemo(() => profileData(seller), [seller]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (oldPassword !== seller?.password) {
      setPasswordError(translate("Current Password is invalid"));
      return;
    }

    if (password !== passwordConfirmation) {
      setPasswordError(translate("Should be equal to New Password"));
      return;
    }
    setPasswordError("");
    setUpdateLoading(true);
    try {
      const res = await ApiRequest.post(
        `update_seller_profile?operation=profile&password=${password}&seller_id=${userDetails.id}`
      );
      if (res.data.status.toLowerCase() === "failed") {
        throw new Error(res.data.reason);
      }
      Toast.fire({
        title: translate("Settings Updated Successfully!"),
        icon: "success",
      });
      await getSellerData(user.id);
      setChangePasswordModal(false);
      setPassword("");
      setOldPassword("");
      setPasswordConfirmation("");
    } catch (err) {
      Toast.fire({
        title: err.message,
        icon: "error",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const stringAvatar = (name) => {
    if (!name) return { children: '?' };
    const str = name.split(" ");
    const returnedValue = str.length === 1 
      ? `${name.split(" ")[0][0]}` 
      : `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`;

    return {
      children: returnedValue,
    };
  };

  const copyTextToClipboard = useCallback(async (text) => {
    if ("clipboard" in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  }, []);

  const handleCopyClick = useCallback((copyText) => {
    copyTextToClipboard(copyText)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 500);
      })
      .catch(() => {
        // Copy failed
      });
  }, [copyTextToClipboard]);

  const options = useMemo(() => {
    if (!filteredData.length) return [];
    return filteredData.map((item) => ({
      ...item,
      value: item.seller_id,
      label: `${item.name} ${item.seller_id}`,
    }));
  }, [filteredData]);

  const handleSellerChosen = async (sellerOrOption) => {
    try {
      const selectedSellerId = sellerOrOption?.seller_id || sellerOrOption?.value || sellerOrOption?.id;
      
      if (!selectedSellerId) return;
      
      // Update the selected option in dropdown
      if (sellerOrOption?.value) {
        setSelectedSellerOption(sellerOrOption);
      } else if (sellerOrOption?.seller_id && options?.length > 0) {
        const option = options.find(opt => opt.value === sellerOrOption.seller_id);
        if (option) setSelectedSellerOption(option);
      }
      
      getAnotherSellerData(selectedSellerId, setSeller);
      getDiscounts(
        user?.id !== selectedSellerId,
        selectedSellerId,
        setDiscountData
      );
    } catch (error) {
      // Seller selection failed
    }
  };

  return (
    <div>
      <Settings isAuthenticated={true} />
      <Dialog 
        open={entryChoiceOpen} 
        onClose={() => setEntryChoiceOpen(false)} 
        keepMounted={false}
        disableEscapeKeyDown={false}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {translate('Select action')}
          <IconButton
            aria-label="close"
            onClick={() => setEntryChoiceOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>{translate('Please choose what you want to do')}</p>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              console.log('📝 UPDATE USER clicked, user.id:', user.id);
              setEntryChoiceOpen(false);
              setHasChosenUpdate(true);
              // Load current user data - seller state will update via useEffect when userDetails loads
              try {
                console.log('🔄 Fetching seller data...');
                await getSellerData(user.id);
                console.log('🔄 Fetching sellers list...');
                await getSellers(isReseller && sellerId);
                console.log('🔄 Fetching discounts...');
                await getDiscounts();
                console.log('✅ All profile data loaded');
              } catch (error) {
                console.error('❌ Error loading profile data:', error);
              }
            }}
          >
            {translate('UPDATE USER')}
          </Button>
          {(isAdmin || isReseller) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setEntryChoiceOpen(false);
                history.push('/signup?admin=1');
              }}
            >
              {translate('NEW USER')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <div
        className={`${!isDarktheme ? "profile-page-modern" : "dark-profile-page-modern"} profile-container-responsive`}
        style={{ width: "100%", maxWidth: "1400px", margin: "auto" }}
      >
        <Box className="profile-header-modern">
          <h2 className="profile-title-modern">{translate("Account Settings")}</h2>
          <p className="profile-subtitle-modern">{translate("Manage your account information and preferences")}</p>
        </Box>

        {isAdmin && (
          <Card className="profile-card-modern admin-selector-card" sx={{ mb: 2, borderRadius: 2, overflow: "visible", zIndex: 100 }}>
            <CardContent sx={{ overflow: "visible", padding: "16px !important" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <h5 style={{ margin: 0, minWidth: "150px" }}>{translate("Change user")}</h5>
                <Box sx={{ flex: 1, minWidth: "300px", zIndex: 100 }}>
                  <Select
                    placeholder={typeof translate("select_seller") === 'string' ? translate("select_seller") : "Search..."}
                    options={options}
                    value={selectedSellerOption}
                    onChange={(e) => handleSellerChosen(e)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "10px",
                        border: "2px solid #e0e0e0",
                        "&:hover": { borderColor: "#45b4e3" }
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <Box className="profile-content-modern">
          {/* NEW USER button removed as per requirement */}
          <Card className="profile-card-modern profile-info-card" sx={{ borderRadius: 2, mb: 2, overflow: "visible" }}>
            <CardContent sx={{ overflow: "visible", padding: "16px !important" }}>
              <Box className="profile-avatar-section-modern">
                {seller && seller.name && (
                  <Box className="avatar-container-modern">
                    <Avatar
                      sx={{
                        width: "100px",
                        height: "100px",
                        fontSize: "32px",
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 6px 16px rgba(102, 126, 234, 0.3)",
                        border: "3px solid white",
                        position: "relative",
                      }}
                    >
                      {stringAvatar(`${seller?.name}`)?.children || (
                        <img 
                          src={require("../../assests/images/icons/UserProfile.jpg").default}
                          alt="User Profile"
                          style={{ 
                            width: "100%", 
                            height: "100%", 
                            objectFit: "cover" 
                          }}
                        />
                      )}
                    </Avatar>
                    <Box className="avatar-info-modern" sx={{ ml: 2 }}>
                      <h3 className="user-name-modern">{seller?.name}</h3>
                      <p className="user-id-modern">ID: {seller?.seller_id || seller?.id}</p>
                    </Box>
                    
                    {/* Contact Options integrated in avatar section */}
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContactOptions sellerId={seller.id} compact={true} />
                    </Box>
                  </Box>
                )}
              </Box>
              
              <Box className="profile-details-section-modern" sx={{ mt: 2 }}>
                {mockChangeProfileData.map((item) =>
                  isAdmin || !item.isAdmin ? (
                    item.aKey === "password" ? (
                      isAdmin ? (
                        <ChangeProfile
                          desc={item.desc}
                          variable={item.variable}
                          userDetails={seller}
                          aKey={item.aKey}
                          checkbox={item.checkbox}
                          enabled={seller?.enabled === "true" ? true : false}
                          getSellerData={handleSellerChosen.bind(null, seller)}
                          selectOptions={item.selectOptions || null}
                        />
                      ) : (
                        (user?.id === (seller?.seller_id || seller?.id) ||
                          isAdmin) && (
                          <Box className="profile-field-modern password-field-modern">
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <label className="field-label-modern">{translate("Password")}</label>
                                <p className="field-value-modern">***********</p>
                              </Box>
                              <Box
                                onClick={() => setChangePasswordModal(true)}
                                sx={{
                                  cursor: "pointer",
                                  color: "#ef4444",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  textDecoration: "underline",
                                  "&:hover": { color: "#dc2626" }
                                }}
                              >
                                {translate("Change Password")}
                              </Box>
                            </Box>
                          </Box>
                        )
                      )
                    ) : item.aKey === "referalCode" ? (
                      <Box className="profile-field-modern referral-field-modern">
                        <label className="field-label-modern">{translate("Referal Code")}</label>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mt: 1,
                            p: 2,
                            backgroundColor: "#f0f9ff",
                            borderRadius: 2,
                            border: "2px solid #e0f2fe"
                          }}
                        >
                          <span
                            className="field-value-modern"
                            style={{
                              flex: 1,
                              color: "#0284c7",
                              fontWeight: 500,
                              fontFamily: "monospace",
                              fontSize: "16px"
                            }}
                          >
                            {seller?.commission_code}
                          </span>
                          <Box
                            onClick={() => {
                              handleCopyClick(
                                `${document.location.origin}/NumberVerification?ref=${seller?.commission_code}`
                              );
                            }}
                            sx={{
                              cursor: "pointer",
                              color: "#0284c7",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              "&:hover": { color: "#0369a1" }
                            }}
                          >
                            <CopyAllIcon />
                            {isCopied && (
                              <span style={{ fontSize: "14px", color: "#10b981" }}>
                                {translate("Copied!")}
                              </span>
                            )}
                          </Box>
                          <ShareIcon
                            sx={{
                              cursor: "pointer",
                              color: "#0284c7",
                              "&:hover": { color: "#0369a1" }
                            }}
                            onClick={() => setShareModal(true)}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <ChangeProfile
                        desc={item.desc}
                        variable={item.variable}
                        userDetails={seller}
                        aKey={item.aKey}
                        checkbox={item.checkbox}
                        disabled={!isAdmin && item.disabled}
                        special={item.special}
                        enabled={seller?.enabled === "true" ? true : false}
                        getSellerData={handleSellerChosen.bind(null, seller)}
                        selectOptions={item.selectOptions || null}
                      />
                    )
                  ) : null
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </div>
      <Modal
        visible={changePasswordModal}
        onClose={() => setChangePasswordModal(false)}
        width={"50vw"}
        title={translate("Change Password")}
      >
        <form className="phone-form" onSubmit={handlePasswordUpdate}>
          <TextFieldGroup
            required={true}
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            label={translate("Current Password")}
            placeholder={translate("Current Password")}
          />
          <TextFieldGroup
            required={true}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={translate("New Password")}
            placeholder={translate("New Password")}
          />
          <TextFieldGroup
            required={true}
            type="password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            label={translate("Confirm New Password")}
            placeholder={translate("Confirm New Password")}
          />
          {passwordError && (
            <small className="form-text text-left" style={{ color: "red" }}>
              {passwordError}
            </small>
          )}
          <div
            className="d-flex align-items-center justify-content-end mt-4"
            style={{ width: "100%" }}
          >
            <button
              className="btn btn-danger "
              onClick={() => setChangePasswordModal(false)}
              style={{ width: 90 }}
            >
              {translate("Cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary mx-3"
              style={{ width: 110 }}
            >
              <div>
                {updateLoading ? (
                  <CircularProgress sx={{ color: "white" }} size={20} />
                ) : (
                  intl.formatMessage({ id: "Update" })
                )}
              </div>
            </button>
          </div>
        </form>
      </Modal>
      {isAdmin && (
        <UsersCompanies
          discountData={discountData}
          setDiscountData={setDiscountData}
          seller={seller}
          getDiscounts={getDiscounts}
          updateData={updateData}
        />
      )}
      <ShareModal
        shareModal={shareModal}
        setShareModal={setShareModal}
        referalCode={`${document.location.origin}/NumberVerification?ref=${seller?.commission_code}`}
      />
      {userDetailsLoading && <Spinner />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  userDetails: state.auth.userDetails,
  userDetailsLoading: state.auth.userDetailsLoading,
  sellers: state.credits.sellers,
  user: state.auth.user,
  userData: state.auth.userData,
  anotherUser: state.auth.anotherUser,
  isDarktheme: state.darkTheme.isDarktheme,
  discounts: state.discounts.discounts,
});

export default connect(mapStateToProps, {
  getSellerData,
  updateProfile,
  getSellers,
  getAnotherSellerData,
  getDiscounts,
})(Profile);
